/**
 * PersistentNotification — A separate notification component that works
 * alongside the existing Toastr. Unlike regular toasts, these notifications:
 *
 *   - Never auto-dismiss (persist until user clicks the dismiss button)
 *   - Always show a dismiss/close button
 *   - Stack alongside regular toasts in the same position
 *
 * It pushes toasts into Toastr's existing container ("generic-toast-message")
 * so persistent and regular toasts share the same stack. No separate
 * ToastContainer is needed here — Toastr already renders one.
 *
 * Triggered via the same setToastMsg dispatch with a `persistent: true` flag:
 *
 *   dispatch(setToastMsg({
 *     msg: "toastMsgs:some_key",
 *     type: "info",
 *     persistent: true,
 *   }));
 *
 * The routing between Toastr and PersistentNotification happens in LoginView.
 */

import _ from "lodash";
import React from "react";
import { toast, Slide } from "react-toastify";
import classes from "./PersistentNotification.scss";
import { InformationFilled, CloseOutlined } from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";

/* Same container ID as Toastr — so persistent notifications stack
   together with regular toasts in the same position. */
const TOASTR_CONTAINER_ID = "generic-toast-message";

const NotificationContent = ({
  closeToast,
  title,
  message,
  actions,
  prefixIcon,
  onDismiss, // optional callback invoked when the user dismisses this notification
}) => {
  const handleClose = () => {
    closeToast(); // remove the toast from the DOM (react-toastify)
    if (onDismiss) {
      onDismiss(); // notify the caller so it can persist the dismissal
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.iconWrapper}>
          {prefixIcon || <InformationFilled size="xx-small" variant="link" />}
        </div>
        <div className={classes.textWrapper}>
          {title && <div className={classes.title}>{title}</div>}
          {message && <div className={classes.message}>{message}</div>}
        </div>
      </div>
      <div className={classes.actions}>
        {actions}
        <IconButton
          icon={<CloseOutlined />}
          size="small"
          variant="plain"
          onClick={handleClose}
        />
      </div>
    </div>
  );
};

class PersistentNotification extends React.PureComponent {
  /**
   * Show a persistent notification.
   * Called from LoginView when the toast payload has `persistent: true`.
   * Pushes into Toastr's container so they stack together.
   *
   * @param {object} props - Same shape as Toastr's showToast params
   *   (title, message, type, variant, actions, prefixIcon, showIcon, onClick)
   */
  showNotification = params => {
    const toastId = _.get(params, "toastId");
    toast(<NotificationContent {...params} />, {
      autoClose: false, // never auto-dismiss
      closeOnClick: false, // only dismiss via the close button, not body clicks
      transition: Slide,
      className: classes.notification,
      containerId: TOASTR_CONTAINER_ID,
      // When a toastId is provided, react-toastify uses it as the key.
      // This allows callers to programmatically dismiss the toast later
      // via toast.dismiss(toastId).
      ...(toastId ? { toastId } : {}),
    });
  };

  /* No render needed — we push into Toastr's existing ToastContainer. */
  render() {
    return null;
  }
}

export default PersistentNotification;
